import { freshModal } from "../freshModal";
import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import { previousBusinessDay } from "../../utils/interviewReminder";
import qualifications from "../../utils/qualifications";
import axios from "../../axios/AxiosInstance";
import { Label } from "./Label";
import { Input } from "./Input";
import LazyLoadingSelect from "./LazyLoadingSelect";
import { toast } from "react-toastify";

const clientAddresses = (client) =>
  [
    ["", "Siège"],
    ["Livraison", "Livraison"],
    ["Facturation", "Facturation"],
  ].flatMap(([suffix, label]) => {
    const street = String(client?.[`address${suffix}`] || "").trim();
    if (!street) return [];
    const city = [client?.[`postCode${suffix}`], client?.[`town${suffix}`]]
      .filter(Boolean)
      .join(" ");
    return [{ label, value: [street, city].filter(Boolean).join(", ") }];
  });

const AddInterviewModalFormSession = ({
  isOpen,
  onClose,
  candidate,
  onSubmit,
  initialClient,
}) => {
  const [formData, setFormData] = useState({
    date: "",
    hour: "",
    client: initialClient?._id || "",
    type: "présentiel",
    comment: "",
    place: clientAddresses(initialClient)[0]?.value || "",
    candidate: candidate._id,
  });

  const [busy, setBusy] = useState(false);
  const [createdInterview, setCreatedInterview] = useState(null);
  const [selectedClient, setSelectedClient] = useState(initialClient || null);
  const addresses = clientAddresses(selectedClient);
  const [clients, setClients] = useState([]);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clientSearch, setClientSearch] = useState(
    initialClient?.companyName || "",
  );
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const timerRef = useRef(null); // Use useRef to hold the debounce timer

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Handle client selection
  const handleClientSelection = (client) => {
    setSelectedClient(client);
    setFormData({
      ...formData,
      client: client._id,
      place: clientAddresses(client)[0]?.value || "",
    });
    setClientSearch(client.companyName); // set the search text to the selected client name
    setShowClientDropdown(false); // hide the dropdown after selection
  };

  // Search clients with debouncing
  const searchClient = (word) => {
    console.log(word);

    setClientSearch(word);
    setSelectedClient(null);
    setFormData((current) => ({ ...current, client: "", place: "" }));
    if (word.length === 0) {
      setClients([]);
    }

    // Do not trigger search if word is too short
    if (word.length < 3) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Clear the previous timer and create a new one
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsLoadingClient(true);
      axios
        .post("client/freelance/find-by-text", { query: word })
        .then((res) => {
          setClients(res.data);
          setShowClientDropdown(true);
          console.log(res.data);
        })
        .catch((e) => console.log(e))
        .finally(() => setIsLoadingClient(false));
    }, 700); // 700ms debounce time
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!formData.client) {
      toast.error("Sélectionnez un client dans les résultats.");
      return;
    }
    const userId = localStorage.getItem("userId");
    if (!userId) {
      toast.error("Reconnectez-vous pour créer le rappel.");
      return;
    }
    setBusy(true);
    let saved = createdInterview;
    try {
      const reminderDate = previousBusinessDay(formData.date);
      if (!saved) {
        const { data } = await axios.post("entretien", formData);
        saved = data || { ...formData };
        setCreatedInterview(saved);
      }
      await axios.post("newtask", {
        type: "Appel",
        priority: "Haute",
        date: reminderDate,
        hour: "09:00",
        comment: `Confirmer l’entretien du ${formData.date} à ${formData.hour} avec ${candidate.firstname || ""} ${candidate.lastname || ""} et ${clientSearch}. ${formData.place || ""} ${formData.comment || ""}`,
        clients: [formData.client],
        candidates: [candidate._id],
        taskCreator: userId,
        taskManagers: [userId],
      });
      toast.success("Entretien et rappel créés.");
      onSubmit?.(saved || formData);
      onClose();
    } catch {
      toast.error(
        saved
          ? "Entretien enregistré, mais rappel non créé. Réessayez pour créer uniquement le rappel."
          : "Impossible d’enregistrer l’entretien.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!busy) onClose();
      }}
      title="Planifier un entretien"
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <fieldset
          disabled={busy || Boolean(createdInterview)}
          className="space-y-4"
        >
          {/* Client */}
          <div className="space-y-2">
            <Label
              htmlFor="client"
              className="text-sm font-medium text-gray-700"
            >
              Client concerné
            </Label>
            <div className="relative">
              <Input
                value={clientSearch}
                onChange={(e) => searchClient(e.target.value)}
                placeholder="Rechercher le client"
                className="w-full border border-gray-300 rounded-md pr-10"
              />
              {isLoadingClient && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <svg
                    className="animate-spin h-4 w-4 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                </div>
              )}
            </div>

            {showClientDropdown && clients.length > 0 && (
              <ul className="absolute z-10 mt-1 w-[400px] bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                {clients.map((client) => (
                  <li
                    key={client._id}
                    onClick={() => handleClientSelection(client)}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                      formData.client?._id === client._id
                        ? "bg-indigo-100 font-semibold"
                        : ""
                    }`}
                  >
                    {client.companyName}
                  </li>
                ))}
              </ul>
            )}

            {formData.client && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  Client sélectionné:
                </span>
                <span className="flex items-center bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
                  {clientSearch}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedClient(null);
                      setFormData({ ...formData, client: "", place: "" });
                      setClientSearch(""); // Clear the search input when deselecting client
                    }}
                    className="ml-2 text-indigo-500 hover:text-indigo-700 focus:outline-none"
                  >
                    &times;
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Other Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                required
                value={formData.date}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label
                htmlFor="hour"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Heure
              </label>
              <input
                type="time"
                id="hour"
                name="hour"
                required
                value={formData.hour}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <div>
              <label
                htmlFor="type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Type d'entretien
              </label>
              <select
                id="type"
                name="type"
                required
                value={formData.type}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="présentiel">Présentiel</option>
                <option value="téléphonique">Téléphonique</option>
                <option value="visioconférence">Visioconférence</option>
              </select>
            </div>
          </div>

          <div>
            <LazyLoadingSelect
              qualifications={qualifications}
              handleChange={handleChange}
              formData={formData}
              setFormData={setFormData}
            />
          </div>

          <div>
            <label
              htmlFor="place"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Lieu
            </label>
            {addresses.length > 0 && (
              <div className="mb-2">
                <label
                  htmlFor="client-address"
                  className="mb-1 block text-xs text-slate-500"
                >
                  Adresses enregistrées du client
                </label>
                <select
                  id="client-address"
                  value={
                    addresses.some(
                      (address) => address.value === formData.place,
                    )
                      ? formData.place
                      : ""
                  }
                  onChange={(e) =>
                    setFormData((current) => ({
                      ...current,
                      place: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Autre lieu</option>
                  {addresses.map((address) => (
                    <option key={address.label} value={address.value}>
                      {address.label} · {address.value}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <input
              type="text"
              id="place"
              name="place"
              placeholder="Adresse de l’entretien ou lien de visioconférence"
              value={formData.place}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="comment"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes complémentaires
            </label>
            <textarea
              id="comment"
              name="comment"
              rows={3}
              value={formData.comment}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </fieldset>
        <p className="text-sm text-slate-500">
          Un rappel sera ajouté à Ma journée à 9 h la veille ouvrée du
          rendez-vous (hors week-ends et jours fériés français).
        </p>
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {busy
              ? "Enregistrement…"
              : createdInterview
                ? "Réessayer le rappel"
                : "Confirmer et créer le rappel"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export const AddInterviewModal = freshModal(
  AddInterviewModalFormSession,
  "isOpen",
);
